import { getV1CompaniesCompanyIdRates, RateOwnerType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCompany } from '~hooks/useCompany/useCompany';
import useLatestRequestSignal from '~hooks/useLatestRequestSignal/useLatestRequestSignal';
import type { Order } from '~hooks/useOrders/models';
import { Rate, type RateBasicWithValue } from '~hooks/useRates/models';
import { rateToRateBasicWithValue } from '~pages/Sales/Orders/NewOrderForm/utils';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import type { Nullable } from '~types/Nullable';

/**
 * Out-of-form preload data for `OrderFormV3Provider`: values the form wants
 * fetched OUTSIDE react-hook-form (the legacy form instead patches
 * these in via `setValue` effects mid-load, forcing extra renders during
 * drawer open).
 *
 * - `geofenceDelayDefault` — the company's `defaultGeofenceDelaySeconds`
 *   fallback, fetched at mount when the order carries none (the legacy
 *   form's own geofence delay seeding). `effects/useGeofenceDelaySeed.ts`
 *   writes it into the `geofenceDelaySeconds` form field once it resolves.
 * - `primaryCustomerRate` — the account's primary customer rate that seeds
 *   the rates UI on new orders (the legacy form's own primary customer rate
 *   fetch). The fetch is account-driven (post-mount), so this hook owns the
 *   state + loader and `effects/usePrimaryCustomerRateFetch.ts` owns the
 *   account/company triggers.
 *
 * `status` mirrors `ProjectFormV2Preloads`: `'loading'` until the mount-time
 * fetches settle, then `'ready'`. Rejections settle too — both fetch paths
 * surface their failure via `connection.handleRequestError` (toast + Sentry)
 * and degrade the preload to `null` rather than wedging the status.
 */
export type OrderFormV3Preloads = {
  status: 'loading' | 'ready';
  geofenceDelayDefault: Nullable<number>;
  primaryCustomerRate: Nullable<RateBasicWithValue>;
  loadPrimaryCustomerRate: (args: {
    accountId: string;
    companyId: string;
  }) => Promise<void>;
  clearPrimaryCustomerRate: () => void;
};

export function useOrderFormV3Preloads({
  defaultOrder,
}: {
  defaultOrder: Order | null;
}): OrderFormV3Preloads {
  const { userStore } = useStores();
  const { getCompanyById } = useCompany();

  // The active membership wins; the user's home company is the fallback.
  // The geofence default is a company-level setting, so the fetch keys on
  // this, not on the form's watched company.
  const currentCompany = userStore.currentCompanies?.[0] ?? userStore.user?.company;
  const currentCompanyId = currentCompany?.id;

  const [geofenceDelayDefault, setGeofenceDelayDefault] =
    useState<Nullable<number>>(null);
  const [geofenceFetchState, setGeofenceFetchState] = useState<
    'idle' | 'loading' | 'settled'
  >('idle');

  // Only fetch while the order has no geofence value of its own. The guard
  // inputs are mount-stable (defaultOrder is a mount prop), so in practice
  // this fetch fires once per drawer open.
  const shouldFetchGeofenceDefault =
    Boolean(currentCompanyId) && !defaultOrder?.geofenceDelaySeconds;

  useEffect(() => {
    if (!shouldFetchGeofenceDefault || !currentCompanyId) {
      return undefined;
    }

    const controller = new AbortController();
    const { signal } = controller;
    setGeofenceFetchState('loading');

    const fetchCompanyDefaults = async () => {
      try {
        const company = await getCompanyById({ id: currentCompanyId, signal });
        if (!signal.aborted && company?.defaultGeofenceDelaySeconds) {
          setGeofenceDelayDefault(company.defaultGeofenceDelaySeconds);
        }
      } catch {
        // getCompanyById already surfaced the failure (handleRequestError
        // toasts + rethrows); the preload degrades to null so `status` can
        // settle instead of wedging at 'loading'.
      } finally {
        if (!signal.aborted) {
          setGeofenceFetchState('settled');
        }
      }
    };

    void fetchCompanyDefaults();

    return () => controller.abort();
    // Legacy deps minus the form bits: the company id and the order's own
    // geofence drive the refire; getCompanyById is not identity-stable
    // across renders and is read via closure like legacy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchGeofenceDefault, currentCompanyId]);

  const [primaryCustomerRate, setPrimaryCustomerRate] =
    useState<Nullable<RateBasicWithValue>>(null);

  // each load (or clear) aborts the one in flight, so only the newest may write
  const issuePrimaryRateRequestSignal = useLatestRequestSignal();

  const loadPrimaryCustomerRate = useCallback(
    async ({ accountId, companyId }: { accountId: string; companyId: string }) => {
      const signal = issuePrimaryRateRequestSignal();

      try {
        // Mirrors useRateDetails.fetchPrimaryCustomerRate: the primary flag
        // is read off the raw DTO, then the winner is parsed into the
        // basic-with-value shape FreightRates consumes.
        const response = await getV1CompaniesCompanyIdRates({
          query: {
            'filter[account_id]': accountId,
            'filter[owner_type]': RateOwnerType.CUSTOMER,
          },
          path: { 'company-id': companyId },
          signal,
        });

        const primaryRate = response.data.data.find((rate) => rate.primary);
        const parsed = primaryRate
          ? rateToRateBasicWithValue(Rate.parse(primaryRate))
          : null;

        if (!signal.aborted) {
          setPrimaryCustomerRate(parsed);
        }
      } catch (error) {
        if (signal.aborted) return;

        setPrimaryCustomerRate(null);
        try {
          connection.handleRequestError(error, t('error_messages.rates.failed_to_fetch'));
        } catch {
          // Surfaced via toast + Sentry; the preload degrades to null.
        }
      }
    },
    [issuePrimaryRateRequestSignal],
  );

  const clearPrimaryCustomerRate = useCallback(() => {
    // aborts the in-flight load, which would otherwise write a stale rate
    issuePrimaryRateRequestSignal();
    setPrimaryCustomerRate(null);
  }, [issuePrimaryRateRequestSignal]);

  const status: OrderFormV3Preloads['status'] =
    geofenceFetchState === 'loading' ? 'loading' : 'ready';

  // Stable identity for the context-value memo (useProjectFormV2Preloads
  // precedent): without this, every Provider render would produce a fresh
  // preloads object and re-render every context consumer.
  return useMemo<OrderFormV3Preloads>(
    () => ({
      status,
      geofenceDelayDefault,
      primaryCustomerRate,
      loadPrimaryCustomerRate,
      clearPrimaryCustomerRate,
    }),
    [
      status,
      geofenceDelayDefault,
      primaryCustomerRate,
      loadPrimaryCustomerRate,
      clearPrimaryCustomerRate,
    ],
  );
}
